import api from '../lib/axios';

// Model management
export async function fetchModels(params?: Record<string, string>) {
  const response = await api.get('/api/admin/models', { params });
  return response.data.data;
}

export async function createModel(data: {
  name: string;
  provider: string;
  modelType: string;
  apiEndpoint: string;
  apiKey: string;
  config?: Record<string, unknown>;
  isEnabled?: boolean;
}) {
  const response = await api.post('/api/admin/models', data);
  return response.data.data;
}

export async function updateModel(id: string, data: Record<string, unknown>) {
  const response = await api.put(`/api/admin/models/${id}`, data);
  return response.data.data;
}

export async function deleteModel(id: string) {
  const response = await api.delete(`/api/admin/models/${id}`);
  return response.data;
}

export async function testModel(id: string) {
  const response = await api.post(`/api/admin/models/${id}/test`);
  return response.data.data;
}

// User management
export async function fetchUsers(params?: Record<string, string | number>) {
  const response = await api.get('/api/admin/users', { params });
  return response.data.data;
}

export async function updateUserRole(id: string, role: string) {
  const response = await api.put(`/api/admin/users/${id}/role`, { role });
  return response.data.data;
}

export async function updateUserStatus(id: string, isActive: boolean) {
  const response = await api.put(`/api/admin/users/${id}/status`, { isActive });
  return response.data.data;
}

export async function deleteUser(id: string) {
  const response = await api.delete(`/api/admin/users/${id}`);
  return response.data;
}

export interface GuestLoginSettings {
  enabled: boolean;
  configured: boolean;
}

export async function fetchGuestLoginSettings(): Promise<GuestLoginSettings> {
  const response = await api.get('/api/admin/users/guest/settings');
  return response.data.data;
}

export async function setGuestLoginEnabled(enabled: boolean): Promise<GuestLoginSettings> {
  const response = await api.put('/api/admin/users/guest/enabled', { enabled });
  return response.data.data;
}

export async function refreshGuestLoginPassword(): Promise<{ password: string; settings: GuestLoginSettings }> {
  const response = await api.post('/api/admin/users/guest/password/refresh');
  return response.data.data;
}

// User Group management
export async function fetchGroups() {
  const response = await api.get('/api/admin/groups');
  return response.data.data;
}

export async function fetchPermissions() {
  const response = await api.get('/api/admin/groups/permissions');
  return response.data.data;
}

export async function createGroup(data: { name: string; description?: string; permissions: string[] }) {
  const response = await api.post('/api/admin/groups', data);
  return response.data.data;
}

export async function updateGroup(id: string, data: { name: string; description?: string; permissions: string[] }) {
  const response = await api.put(`/api/admin/groups/${id}`, data);
  return response.data.data;
}

export async function deleteGroup(id: string) {
  const response = await api.delete(`/api/admin/groups/${id}`);
  return response.data.data;
}

export async function duplicateGroup(id: string, data: { name: string; description?: string }) {
  const response = await api.post(`/api/admin/groups/${id}/duplicate`, data);
  return response.data.data;
}

export async function updateUserGroup(userId: string, groupId: string | null) {
  const response = await api.put(`/api/admin/users/${userId}/group`, { groupId });
  return response.data.data;
}

// Usage Estimation
export async function fetchUsageEstimation(params?: { startDate?: string; endDate?: string; groupId?: string }) {
  const response = await api.get('/api/admin/usage', { params });
  return response.data.data;
}

// System Config
export async function fetchConfigs(keys: string[]) {
  const response = await api.get('/api/admin/configs', { params: { keys: keys.join(',') } });
  return response.data.data;
}

export async function setConfig(key: string, value: any, description?: string) {
  const response = await api.post('/api/admin/configs', { key, value, description });
  return response.data.data;
}

export async function deleteConfig(key: string) {
  const response = await api.delete(`/api/admin/configs/${key}`);
  return response.data;
}

export interface QuotaData {
  used: number;
  limit: number | null;
}

export async function fetchMyQuota(): Promise<QuotaData> {
  const response = await api.get('/api/users/me/quota');
  return response.data.data;
}

export type ResourceRepairRecordStatus = 'REPAIRED' | 'FAILED' | 'INVALID';

export interface ResourceRepairRecord {
  id: string;
  resourceId: string;
  sourceUrl: string;
  status: ResourceRepairRecordStatus;
  attempts: number;
  error: string | null;
  repairedFileUrl: string | null;
  repairedObjpath: string | null;
  createdAt: string;
  completedAt: string | null;
  resourceType: 'IMAGE' | 'VIDEO' | 'AUDIO' | null;
  resourceStatus: 'ACTIVE' | 'DELETED' | null;
  projectName: string | null;
}

export interface ResourceRepairRecordsResult {
  items: ResourceRepairRecord[];
  total: number;
  summary: Record<string, number>;
}

export async function fetchResourceRepairRecords(params: {
  page: number;
  pageSize: number;
  status?: ResourceRepairRecordStatus;
}): Promise<ResourceRepairRecordsResult> {
  const response = await api.get('/api/admin/resource-repairs', { params });
  return response.data.data;
}

export interface ResourceRepairSchedulerState {
  running: boolean;
  cursor: string | null;
  lastTickAt: string | null;
  lastResult: Record<string, unknown> | null;
  intervalMs: number;
  initialDelayMs: number;
  batchSize: number;
  itemDelayMs: number;
}

export async function fetchResourceRepairSchedulerState(): Promise<ResourceRepairSchedulerState> {
  const response = await api.get('/api/admin/resource-repairs/scheduler');
  return response.data.data;
}
